import React, { memo, useCallback, useRef } from "react";

export type TabType = 'risk-insights' | 'health-data';

export interface Tab {
  id: TabType;
  label: string;
  tabContent: React.ReactElement;
  heroContent?: React.ReactElement;
}

interface HealthDataTabsProps {
  activeTab: Tab;
  onTabChange: (tabId: TabType) => void;
  tabs: Tab[];
}

const HealthDataTabs = memo(function HealthDataTabs({ activeTab, onTabChange, tabs }: HealthDataTabsProps) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleTabClick = useCallback((tabId: TabType) => {
    onTabChange(tabId);
  }, [onTabChange]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent, currentIndex: number) => {
    let newIndex = currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
        break;
      case 'ArrowRight':
        event.preventDefault();
        newIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    const newTab = tabs[newIndex];
    onTabChange(newTab.id);
    tabRefs.current[newIndex]?.focus();
  }, [tabs, onTabChange]);


  return (
    <div className="bg-white/5 rounded-[15px] p-[5px]">
      {/* Tab Navigation */}
      <div className="flex gap-[10px] relative" role="tablist" aria-label="Health data navigation tabs">
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            id={`tab-${tab.id}`}
            ref={(el) => tabRefs.current[index] = el}
            onClick={() => handleTabClick(tab.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            role="tab"
            aria-selected={activeTab.id === tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            tabIndex={activeTab.id === tab.id ? 0 : -1}
            type="button"
            className={`
              flex items-center justify-center px-[23px] py-[9px] rounded-[4.71px]
              h-[39px] w-[156px] text-[17px] text-white whitespace-nowrap
              transition-all duration-200 relative z-10
              ${activeTab.id === tab.id
                ? 'font-bold'
                : 'font-normal hover:bg-white/5'
              }
            `}
          >
            {tab.label}
          </button>
        ))}

        {/* Active Tab Background */}
        <div
          className="absolute bg-white/10 h-[39px] w-[156px] rounded-[8.71px] top-0 transition-transform duration-300 ease-out"
          style={{
            transform: activeTab.id === 'risk-insights' ? 'translateX(0px)' : 'translateX(166px)'
          }}
        />
      </div>
    </div>
  );
});

export default HealthDataTabs;
